<template>
  <div>
    <SetupPage
        page-label="Grade Scale"
        eyebrow="ASSESSMENT STRUCTURE"
        title="Grade scale"
        description="Define the grade bands used on report cards — the grade, its title and the mark range it covers."
        toolbar-title="Grade bands"
        :meta="scopeLabel"
        :loader="onloader"
    >
        <template slot="side">
            <div v-if="selectedCategoryId && gradeitems !== null" class="ws-stat">
                <strong>{{ grades.length }}</strong>
                <span>{{ grades.length === 1 ? "grade band" : "grade bands" }}</span>
            </div>
            <button
                type="button"
                class="btn ws-primary-button"
                :disabled="!categories.length"
                :title="categories.length ? 'Add a grade band' : 'Create a class category first'"
                @click="openCreate"
            >
                <i class="fa fa-plus" aria-hidden="true"></i>
                <span>New grade</span>
            </button>
        </template>

        <template slot="toolbar">
            <label class="ws-scope">
                <span>Category</span>
                <select
                    v-model="selectedCategoryId"
                    :disabled="categoriesLoading || !categories.length"
                    aria-label="Class category to show grade bands for"
                    @change="onCategoryChange"
                >
                    <option v-if="categoriesLoading" :value="0" disabled>Loading categories…</option>
                    <option v-else :value="0">Select category</option>
                    <option v-for="category in categories" :key="category.Id" :value="category.Id">
                        {{ category.CategoryName }}
                    </option>
                </select>
            </label>

            <div class="ws-search">
                <i class="fa fa-search" aria-hidden="true"></i>
                <input
                    v-model="searchval"
                    type="search"
                    placeholder="Search grade or title"
                    aria-label="Search grade bands by grade or title"
                    :disabled="!grades.length"
                />
                <button
                    v-if="searchval"
                    type="button"
                    class="ws-search__clear"
                    aria-label="Clear search"
                    @click="searchval = ''"
                >
                    <i class="fa fa-times" aria-hidden="true"></i>
                </button>
            </div>
        </template>

        <div v-if="categoriesLoading" class="ws-state">
            <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
            <strong>Loading categories</strong>
            <span>Fetching the class categories available at your school.</span>
        </div>

        <div v-else-if="categoriesFailed" class="ws-state">
            <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
            <strong>Categories could not be loaded</strong>
            <span>Check your connection and try again.</span>
            <button type="button" class="btn ws-ghost-button" @click="retryCategories">
                <i class="fa fa-refresh" aria-hidden="true"></i>
                <span>Try again</span>
            </button>
        </div>

        <div v-else-if="!categories.length" class="ws-state">
            <i class="fa fa-layer-group" aria-hidden="true"></i>
            <strong>No class categories available</strong>
            <span>Set up class categories before defining a grade scale.</span>
        </div>

        <div v-else-if="!selectedCategoryId" class="ws-state">
            <i class="fa fa-filter" aria-hidden="true"></i>
            <strong>Select a category</strong>
            <span>Choose a category above to see its grade bands.</span>
        </div>

        <div v-else-if="gradesLoading && gradeitems === null" class="ws-state">
            <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
            <strong>Loading grade bands</strong>
            <span>Fetching the grade scale for {{ selectedCategoryName }}.</span>
        </div>

        <div v-else-if="gradesFailed" class="ws-state">
            <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
            <strong>Grade bands could not be loaded</strong>
            <span>Try loading the grade scale for {{ selectedCategoryName }} again.</span>
            <button type="button" class="btn ws-ghost-button" @click="retryGrades">
                <i class="fa fa-refresh" aria-hidden="true"></i>
                <span>Try again</span>
            </button>
        </div>

        <div v-else-if="!grades.length" class="ws-state">
            <i class="fa fa-list-ul" aria-hidden="true"></i>
            <strong>No grade bands for {{ selectedCategoryName }}</strong>
            <span>Register the first band, for example A for 70–100.</span>
            <button type="button" class="btn ws-ghost-button" @click="openCreate">
                <i class="fa fa-plus" aria-hidden="true"></i>
                <span>Add grade band</span>
            </button>
        </div>

        <div v-else-if="!filteredGrades.length" class="ws-state">
            <i class="fa fa-search" aria-hidden="true"></i>
            <strong>No grade bands match “{{ searchval.trim() }}”</strong>
            <span>Try a different grade or title.</span>
            <button type="button" class="btn ws-ghost-button" @click="searchval = ''">
                Clear search
            </button>
        </div>

        <div v-else class="ws-grid" :class="{ 'is-loading': gradesLoading }">
            <article
                v-for="(item, index) in filteredGrades"
                :key="rowKey(item, index)"
                class="ws-card"
            >
                <span class="ws-card__index">{{ item.Grade || "—" }}</span>
                <div class="ws-card__body">
                    <span class="ws-card__title">{{ item.Title || "Untitled grade" }}</span>
                    <span class="ws-card__meta">{{ formatRange(item) }}</span>
                </div>
                <div class="ws-card__actions">
                    <button
                        type="button"
                        class="ws-edit-button"
                        :aria-label="editLabel(item)"
                        @click="editGrade(item)"
                    >
                        <i class="fa fa-pencil" aria-hidden="true"></i>
                        <span>Edit</span>
                    </button>
                </div>
            </article>
        </div>
    </SetupPage>

    <SetupDrawer
        :show.sync="formVisible"
        icon="fa-layer-group"
        :eyebrow="editingId ? 'UPDATE RECORD' : 'NEW RECORD'"
        :title="editingId ? 'Edit grade band' : 'Add a grade band'"
        :subtitle="drawerSubtitle"
        :saving="isSaving"
        :submit-label="editingId ? 'Save changes' : 'Create grade'"
        :submit-disabled="!categories.length"
        form-id="grade-form"
        @close="closeForm"
        @submit="submitGrade"
    >
        <fieldset class="ws-fieldset">
            <legend>Grade details</legend>

            <div class="row">
                <div class="col-sm-6">
                    <div class="form-group">
                        <label for="grade-category">
                            Category <span class="ws-required">*</span>
                        </label>
                        <select
                            id="grade-category"
                            v-model="form.categoryId"
                            class="form-control"
                            :class="{ 'is-invalid': errors.categoryId }"
                            :disabled="isSaving || !categories.length"
                            @change="clearError('categoryId')"
                        >
                            <option :value="0">Select category</option>
                            <option v-for="category in categories" :key="category.Id" :value="category.Id">
                                {{ category.CategoryName }}
                            </option>
                        </select>
                        <span v-if="errors.categoryId" class="ws-error" role="alert">
                            {{ errors.categoryId }}
                        </span>
                    </div>
                </div>

                <div class="col-sm-6">
                    <div class="form-group">
                        <label for="grade-name">
                            Grade <span class="ws-required">*</span>
                        </label>
                        <input
                            id="grade-name"
                            v-model="form.grade"
                            type="text"
                            class="form-control"
                            :class="{ 'is-invalid': errors.grade }"
                            placeholder="e.g. A"
                            maxlength="10"
                            autocomplete="off"
                            :disabled="isSaving"
                            @input="clearError('grade')"
                        />
                        <span v-if="errors.grade" class="ws-error" role="alert">
                            {{ errors.grade }}
                        </span>
                    </div>
                </div>

                <div class="col-12">
                    <div class="form-group">
                        <label for="grade-title">
                            Title <span class="ws-required">*</span>
                        </label>
                        <input
                            id="grade-title"
                            v-model="form.title"
                            type="text"
                            class="form-control"
                            :class="{ 'is-invalid': errors.title }"
                            placeholder="e.g. Excellent"
                            maxlength="80"
                            autocomplete="off"
                            :disabled="isSaving"
                            @input="clearError('title')"
                        />
                        <span v-if="errors.title" class="ws-error" role="alert">
                            {{ errors.title }}
                        </span>
                        <span v-else class="ws-hint">
                            Shown next to the grade on report cards.
                        </span>
                    </div>
                </div>

                <div class="col-6">
                    <div class="form-group">
                        <label for="grade-from">
                            Mark from <span class="ws-required">*</span>
                        </label>
                        <input
                            id="grade-from"
                            v-model.number="form.markFrom"
                            type="number"
                            min="0"
                            step="1"
                            inputmode="numeric"
                            class="form-control"
                            :class="{ 'is-invalid': errors.markFrom }"
                            :disabled="isSaving"
                            @input="clearError('markFrom')"
                        />
                        <span v-if="errors.markFrom" class="ws-error" role="alert">
                            {{ errors.markFrom }}
                        </span>
                    </div>
                </div>

                <div class="col-6">
                    <div class="form-group">
                        <label for="grade-to">
                            Mark to <span class="ws-required">*</span>
                        </label>
                        <input
                            id="grade-to"
                            v-model.number="form.markTo"
                            type="number"
                            min="0"
                            step="1"
                            inputmode="numeric"
                            class="form-control"
                            :class="{ 'is-invalid': errors.markTo }"
                            :disabled="isSaving"
                            @input="clearError('markTo')"
                        />
                        <span v-if="errors.markTo" class="ws-error" role="alert">
                            {{ errors.markTo }}
                        </span>
                    </div>
                </div>
            </div>

            <p class="ws-fieldset__note">
                Mark ranges are inclusive. “Mark from” must be lower than or equal to “Mark to”.
            </p>
        </fieldset>
    </SetupDrawer>
  </div>
</template>

<script>
import Alert from "@/mixins/Alert.mixins";
import SetupPage from "@/components/SetupPage";
import SetupDrawer from "@/components/SetupDrawer";

const EMPTY_FORM = () => ({
    categoryId: 0,
    grade: "",
    title: "",
    markFrom: "",
    markTo: ""
});

export default {
    name: "grade-scale",
    mixins: [Alert],
    components: {
        SetupPage,
        SetupDrawer
    },
    data() {
        return {
            accessDetails: null,
            classcategory: null,
            selectedCategoryId: 0,
            gradeitems: null,
            searchval: "",
            categoriesFailed: false,
            gradesLoading: false,
            gradesFailed: false,
            requestSeq: 0,
            formVisible: false,
            isSaving: false,
            editingId: 0,
            form: EMPTY_FORM(),
            errors: {},
            onloader: "none"
        };
    },
    computed: {
        categories() {
            return Array.isArray(this.classcategory) ? this.classcategory : [];
        },
        grades() {
            return Array.isArray(this.gradeitems) ? this.gradeitems : [];
        },
        categoriesLoading() {
            return this.classcategory === null;
        },
        selectedCategoryName() {
            const selected = this.categories.find(
                item => String(item.Id) === String(this.selectedCategoryId)
            );
            return selected ? selected.CategoryName : "";
        },
        formCategoryName() {
            const selected = this.categories.find(
                item => String(item.Id) === String(this.form.categoryId)
            );
            return selected ? selected.CategoryName : "";
        },
        hasSearch() {
            return this.searchval.trim().length > 0;
        },
        filteredGrades() {
            const tokens = this.searchval
                .toLowerCase()
                .split(/\s+/)
                .filter(Boolean);

            if (!tokens.length) return this.grades;

            return this.grades.filter(item => {
                const haystack = [item.Grade, item.Title]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                return tokens.every(token => haystack.includes(token));
            });
        },
        scopeLabel() {
            if (this.categoriesLoading) return "Loading categories…";
            if (this.categoriesFailed) return "Categories unavailable.";
            if (!this.categories.length) return "No categories available.";
            if (!this.selectedCategoryId) return "Choose a category to view its grade bands.";
            if (this.gradesLoading) return `${this.selectedCategoryName} · loading grade bands…`;
            if (this.gradesFailed) return `${this.selectedCategoryName} · grade bands unavailable`;
            if (this.hasSearch) {
                return `${this.selectedCategoryName} · ${this.filteredGrades.length} of ${this.grades.length} shown`;
            }
            const count = this.grades.length;
            return `${this.selectedCategoryName} · ${count} ${count === 1 ? "grade band" : "grade bands"}`;
        },
        drawerSubtitle() {
            return [this.form.grade, this.formCategoryName].filter(Boolean).join(" · ");
        }
    },
    methods: {
        rowKey(item, index) {
            if (item && item.Id !== undefined && item.Id !== null && item.Id !== "") {
                return item.Id;
            }
            return `grade-${index}`;
        },
        editLabel(item) {
            const label = [item && item.Grade, item && item.Title].filter(Boolean).join(" — ");
            return label ? `Edit ${label}` : "Edit grade band";
        },
        displayMark(value) {
            if (value === null || value === undefined || value === "") return "—";
            return String(value);
        },
        formatRange(item) {
            const from = this.displayMark(item.Markfrom);
            const to = this.displayMark(item.Markto);
            if (from === "—" && to === "—") return "No mark range set";
            return `${from} – ${to}`;
        },
        errorMessage(error, fallback) {
            const data = error && error.response && error.response.data;
            if (typeof data === "string" && data.trim()) return data.trim();
            if (data && typeof data === "object") {
                return data.message || data.responseMessage || fallback;
            }
            if (error && error.message) return error.message;
            return fallback;
        },
        async loadCategories() {
            this.classcategory = null;
            this.categoriesFailed = false;

            try {
                const resp = await this.$store.dispatch("getclasscategory");
                this.classcategory = Array.isArray(resp) ? resp : [];

                const exists = this.classcategory.some(
                    item => String(item.Id) === String(this.selectedCategoryId)
                );
                if (!exists) {
                    this.selectedCategoryId = this.classcategory.length
                        ? this.classcategory[0].Id
                        : 0;
                }

                if (this.selectedCategoryId) {
                    await this.loadGrades();
                } else {
                    this.gradeitems = null;
                }
            } catch (error) {
                this.classcategory = [];
                this.categoriesFailed = true;
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to load class categories."),
                    ""
                );
            }
        },
        async loadGrades() {
            const categoryId = this.selectedCategoryId;
            if (!categoryId) {
                this.gradeitems = null;
                this.gradesLoading = false;
                return;
            }

            const sequence = ++this.requestSeq;
            this.gradesLoading = true;
            this.gradesFailed = false;

            try {
                const resp = await this.$store.dispatch("getgradescale", categoryId);
                if (sequence !== this.requestSeq) return;
                this.gradeitems = Array.isArray(resp) ? resp : [];
            } catch (error) {
                if (sequence !== this.requestSeq) return;
                this.gradesFailed = true;
                this.gradeitems = [];
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to load grade bands for this category."),
                    ""
                );
            } finally {
                if (sequence === this.requestSeq) this.gradesLoading = false;
            }
        },
        retryCategories() {
            this.loadCategories();
        },
        retryGrades() {
            this.gradesFailed = false;
            this.gradeitems = null;
            this.loadGrades();
        },
        onCategoryChange() {
            this.searchval = "";
            this.gradeitems = null;
            this.gradesFailed = false;
            this.loadGrades();
        },
        resetForm() {
            this.editingId = 0;
            this.errors = {};
            this.form = EMPTY_FORM();
        },
        openCreate() {
            if (!this.categories.length) return;
            this.resetForm();
            this.form = {
                categoryId: this.selectedCategoryId || 0,
                grade: "",
                title: "",
                markFrom: "",
                markTo: ""
            };
            this.formVisible = true;
        },
        async editGrade(item) {
            if (!item) return;
            this.onloader = "flex";
            try {
                const resp = await this.$store.dispatch("getgradescalebyid", item.Id);
                if (!resp) throw new Error("The selected grade band could not be loaded.");

                this.resetForm();
                this.editingId = item.Id || 0;
                this.form = {
                    categoryId: resp.CategoryId || item.CategoryId || this.selectedCategoryId || 0,
                    grade: resp.Grade || item.Grade || "",
                    title: resp.Title || item.Title || "",
                    markFrom:
                        resp.Markfrom !== null && resp.Markfrom !== undefined
                            ? resp.Markfrom
                            : "",
                    markTo:
                        resp.Markto !== null && resp.Markto !== undefined ? resp.Markto : ""
                };
                this.formVisible = true;
            } catch (error) {
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to load this grade band."),
                    ""
                );
            } finally {
                this.onloader = "none";
            }
        },
        closeForm() {
            this.formVisible = false;
            this.resetForm();
        },
        clearError(field) {
            if (!this.errors[field]) return;
            const errors = { ...this.errors };
            delete errors[field];
            this.errors = errors;
        },
        toNumber(value) {
            if (value === null || value === undefined || value === "") return null;
            const parsed = Number(value);
            return Number.isFinite(parsed) ? parsed : null;
        },
        validate() {
            const errors = {};

            const categoryId = this.form.categoryId;
            if (
                categoryId === 0 ||
                categoryId === "0" ||
                categoryId === "" ||
                categoryId === null ||
                categoryId === undefined
            ) {
                errors.categoryId = "Choose the category for this grade band.";
            }

            if (!String(this.form.grade || "").trim()) {
                errors.grade = "Enter the grade, for example A.";
            }

            if (!String(this.form.title || "").trim()) {
                errors.title = "Enter a title for this grade band.";
            }

            const markFrom = this.toNumber(this.form.markFrom);
            const markTo = this.toNumber(this.form.markTo);

            if (markFrom === null) {
                errors.markFrom = "Enter the lower mark.";
            } else if (markFrom < 0) {
                errors.markFrom = "Marks cannot be negative.";
            }

            if (markTo === null) {
                errors.markTo = "Enter the upper mark.";
            } else if (markTo < 0) {
                errors.markTo = "Marks cannot be negative.";
            } else if (markFrom !== null && markTo < markFrom) {
                errors.markTo = "The upper mark must be greater than or equal to the lower mark.";
            }

            this.errors = errors;

            if (Object.keys(errors).length) {
                const fieldIds = {
                    categoryId: "grade-category",
                    grade: "grade-name",
                    title: "grade-title",
                    markFrom: "grade-from",
                    markTo: "grade-to"
                };
                const firstField = Object.keys(errors)[0];
                const element = document.getElementById(fieldIds[firstField]);
                if (element) element.focus();
            }

            return Object.keys(errors).length === 0;
        },
        async submitGrade() {
            if (this.isSaving) return;
            if (!this.validate()) return;

            const isEdit = Boolean(this.editingId);
            const savedCategory = this.form.categoryId;
            const payload = {
                Id: this.editingId || 1,
                CategoryId: savedCategory,
                Grade: String(this.form.grade || "").trim(),
                GradeTitle: String(this.form.title || "").trim(),
                MarkFrom: Number(this.form.markFrom),
                MarkTo: Number(this.form.markTo),
                processop: isEdit ? "U" : "N",
                username: this.$store.state.auth.username,
                accesskey: this.$store.state.auth.accesskey
            };

            this.isSaving = true;
            this.onloader = "flex";
            try {
                const resp = await this.$store.dispatch("gradescaleregistration", payload);

                if (resp && resp.responseCode && resp.responseCode !== "00") {
                    this.ShowAlertError(
                        resp.responseMessage || "The grade band could not be saved.",
                        ""
                    );
                    return;
                }

                this.closeForm();
                this.searchval = "";
                this.selectedCategoryId = savedCategory;
                this.gradeitems = null;
                await this.loadGrades();

                if (this.gradesFailed) {
                    this.ShowAlertWarning(
                        "Grade band saved, but the updated list could not be loaded.",
                        ""
                    );
                } else {
                    this.ShowAlertSuccess(
                        isEdit ? "Grade band updated successfully." : "Grade band created successfully.",
                        ""
                    );
                }
            } catch (error) {
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to save this grade band at the moment."),
                    ""
                );
            } finally {
                this.isSaving = false;
                this.onloader = "none";
            }
        }
    },
    mounted() {
        const userDetails = this.$store.getters["auth/_user"];
        if (!userDetails || userDetails.isSchoolStaff == false) {
            this.$store.dispatch("auth/Logout");
            this.$router.push("/login");
            return;
        }
        this.loadCategories();
    }
};
</script>
